import { createContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import SweetAlert2 from "react-sweetalert2";
import { Region } from "../../../constants/CommonConst";
import { LanguageDDLConst } from "../../../constants/LanguageLocale";
import { NavBarConst } from "../../../constants/NavBarConst";
import { ApiStatusTypeEnum } from "../../../enumeration/ApiStatusTypeEnum";
import { setLanguage } from "../../../i18n/Metronici18n";
import { checkIsIndoRegion, toAbsoluteUrl } from "../../../utils/CommonHelper";
import { useContactUsHelper } from "../../../utils/ContactUsHelper";
import { useAuth } from "../../auth/components/AuthInit";
import * as actions from "../redux/MasterLayoutAction";
import { ContactUsModal } from "./ContactUsModal";
import { Overlay } from "./Overlay";

const MasterLayoutContext = createContext();

export const SettingIcon = ({ url, icon, iconActive, title, navigate }) => {
  const location = useLocation();
  const path = location.pathname;
  const intl = useIntl();

  return (
    <img
      className="navbar-icon me-2 cursor-pointer"
      src={toAbsoluteUrl(path === url ? iconActive : icon)}
      onClick={() => {
        if (navigate) {
          navigate(url);
        }
      }}
      onMouseOver={(e) => {
        e.currentTarget.src = toAbsoluteUrl(iconActive);
      }}
      onMouseLeave={(e) => {
        if (path !== url) e.currentTarget.src = toAbsoluteUrl(icon);
      }}
      alt={intl.formatMessage({ id: title })}
      title={intl.formatMessage({ id: title })}
    />
  );
};

export const MasterLayout = () => {
  const intl = useIntl();
  const location = useLocation();
  const path = location.pathname;
  const navigate = useNavigate();
  const { auth, saveAuth } = useAuth();
  const [swalProps, setSwalProps] = useState({});
  const masterLayoutState = useSelector(
    ({ masterLayout }) => masterLayout,
    shallowEqual,
  );

  const [isRefreshClick, setIsRefreshClick] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    LanguageDDLConst.find(
      (languageDDL) => languageDDL.language === auth.language,
    ),
  );
  const isIndoRegion = checkIsIndoRegion(auth);
  const dispatch = useDispatch();
  const value = {};

  useEffect(() => {
    dispatch(actions.getWalletInfo());
  }, []);

  useEffect(() => {
    setSelectedLanguage(
      LanguageDDLConst.find(
        (languageDDL) => languageDDL.language === auth.language,
      ),
    );
  }, [auth]);

  useEffect(() => {
    if (selectedLanguage && selectedLanguage.language !== auth.language) {
      const queryParams = {
        language: selectedLanguage.language,
      };

      dispatch(actions.updateLanguage(queryParams)).then((resp) => {
        resp = resp.data;
        if (parseInt(resp.code) === ApiStatusTypeEnum.SUCCESS) {
          setSwalProps({
            show: true,
            icon: "success",
            title: intl.formatMessage({ id: "success" }),
            text: intl.formatMessage({ id: "changeLanguageSuccess" }),
            onResolve: () => {
              setLanguage(selectedLanguage.language);
              setSwalProps({});
              saveAuth({ ...auth, language: selectedLanguage.language });
            },
          });
        } else {
          setSwalProps({
            show: true,
            icon: "error",
            title: intl.formatMessage({ id: "error" }),
            text: resp.message,
            onResolve: () => {
              setSwalProps({});
              setSelectedLanguage(
                LanguageDDLConst.find(
                  (languageDDL) => languageDDL.language === auth.language,
                ),
              );
            },
          });
        }
      });
    }
  }, [selectedLanguage]);

  useContactUsHelper(masterLayoutState);

  return (
    <MasterLayoutContext.Provider value={value}>
      <SweetAlert2
        onResolve={() => {
          setSwalProps({});
        }}
        backdrop={false}
        {...swalProps}
        customClass="custom-swal"
      />
      {swalProps.show && <Overlay zIndex={1} />}
      <ContactUsModal setSwalProps={setSwalProps} />
      <div className="rc279-header">
        <div className="px-4 py-3">
          <div className="d-flex mt-3 justify-content-between align-items-center">
            <div className="d-flex">
              <img
                src={toAbsoluteUrl("images/logo.png")}
                alt="logo"
                style={{ width: "150px" }}
                className="me-4"
              />
              {Object.entries(NavBarConst)
                .filter(([key]) =>
                  isIndoRegion ? key !== "lottery" && key !== "fishing" : true,
                )
                .map(([key, value]) => (
                  <div
                    className={`navbar-item cursor-pointer pb-2 ${
                      path === value ? "active" : ""
                    }`}
                    onClick={() => {
                      navigate(value);
                    }}
                    key={key}
                  >
                    {intl.formatMessage({ id: key })}
                  </div>
                ))}
            </div>
            <div className="d-flex align-items-center">
              <SettingIcon
                url="/setting/deposit"
                icon="images/icon/topup/topup.png"
                iconActive="images/icon/topup/topup_select.png"
                title="deposit"
                navigate={navigate}
              />
              <SettingIcon
                url="/setting/withdraw"
                icon="images/icon/withdraw/withdraw.png"
                iconActive="images/icon/withdraw/withdraw_select.png"
                title="withdraw"
                navigate={navigate}
              />
              <SettingIcon
                url="/setting/gameTransfer"
                icon="images/icon/transfer/gameTransfer.png"
                iconActive="images/icon/transfer/gameTransfer_select.png"
                title="gameTransfer"
                navigate={navigate}
              />
              <div className="d-flex">
                <div className="triangle-bottomright" />
                <div className="text-end ps-2 navbar-wallet d-flex align-items-center">
                  <div>
                    <div className="ws-nowrap">
                      {auth.username ? auth.username.toUpperCase() : null}
                    </div>
                    <div className="fw-bold ws-nowrap d-flex align-items-start">
                      <div className="text-start">
                        <div
                          className="me-1"
                          style={{ borderBottom: "1px solid black" }}
                        >
                          {auth.currencycode}{" "}
                          {masterLayoutState.walletInfo
                            ? masterLayoutState.walletInfo.balance
                            : intl.formatMessage({ id: "loading" })}
                        </div>
                        <div className="me-1" style={{ fontSize: "11px" }}>
                          {intl.formatMessage({ id: "game" })} :{" "}
                          {masterLayoutState.walletInfo
                            ? masterLayoutState.walletInfo.gamebalance
                            : intl.formatMessage({ id: "loading" })}
                        </div>
                      </div>
                      <i
                        style={{ marginTop: "0.3rem" }}
                        className={`fa fa-refresh cursor-pointer ${
                          isRefreshClick ? "spinning-animation" : ""
                        }`}
                        onClick={(e) => {
                          dispatch(actions.getWalletInfo());
                          setIsRefreshClick(true);

                          setTimeout(() => {
                            setIsRefreshClick(false);
                          }, 1000);
                        }}
                      />
                    </div>
                  </div>

                  <img
                    className="navbar-icon mx-2 cursor-pointer"
                    src={toAbsoluteUrl("images/icon/profile/user.png")}
                    style={{ height: "45px", width: "45px" }}
                    onClick={() => {
                      navigate("/setting/profile");
                    }}
                  />
                </div>
              </div>
              <div className="ms-3">
                <div className="dropdown">
                  <button
                    className="border-0"
                    style={{ background: "transparent" }}
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    data-text={intl.formatMessage({ id: "language" })}
                  >
                    <img
                      src={toAbsoluteUrl(
                        `images/language/${selectedLanguage.language}.png`,
                      )}
                      alt={intl.formatMessage({
                        id: selectedLanguage.title,
                      })}
                      className="language-icon"
                    />
                  </button>
                  <div className="dropdown-menu w-100">
                    {LanguageDDLConst.map((languageDDL) => (
                      <div
                        className={`dropdown-item d-flex align-items-center py-2 cursor-pointer ${
                          selectedLanguage &&
                          selectedLanguage.language === languageDDL.language
                            ? "active"
                            : ""
                        }`}
                        key={languageDDL.language}
                        onClick={() => {
                          document
                            .querySelector(".dropdown-menu")
                            .classList.remove("show");
                          setSelectedLanguage(languageDDL);
                        }}
                      >
                        <img
                          src={toAbsoluteUrl(
                            `images/language/${languageDDL.language}.png`,
                          )}
                          alt={intl.formatMessage({ id: languageDDL.title })}
                          className="me-4 language-icon"
                        />
                        <div>
                          {intl.formatMessage({ id: languageDDL.title })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="rc279-content">
        <Outlet />
        {!path.includes("/gamePlay") && (
          <>
            {masterLayoutState.contactList &&
              masterLayoutState.contactList.countrycode === Region.MALAYSIA && (
                <div
                  className="cursor-pointer position-fixed rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                  data-bs-toggle="modal"
                  data-bs-target="#liveChatModal"
                  style={{
                    width: "50px",
                    height: "50px",
                    bottom: "2rem",
                    right: "1.55rem",
                  }}
                >
                  <i className="bi bi-chat fs-5 mb-2" />
                </div>
              )}
          </>
        )}
      </div>
    </MasterLayoutContext.Provider>
  );
};
